import { useMemo } from 'react';

import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import CloseIcon from '@mui/icons-material/Close';
import { Box, IconButton, ListItemText, Tooltip } from '@mui/material';

import { MAX_SORT_COLUMNS } from '@/shared/lib/sorting';

import { ContextMenuSortIcon } from '../GeneratikTable.icons';
import { useGeneratikTableContext } from '../GeneratikTableContext';
import { sortLevelBadgeSx, sortLevelIconButtonSx } from '../ToolbarSortMenu.styles';
import { CONTEXT_MENU_DESIGN } from '../contextMenuDesign';
import ContextSubmenu from './ContextSubmenu';
import { buildSortMenuItem } from './sortMenuItem';
import {
  CONTEXT_MENU_TESTIDS,
  sortOptionTestId,
  sortPriorityTestId,
  sortRemoveTestId,
} from './tableContextMenu.constants';
import { ContextMenuItem } from './tableContextMenu.styles';
import { deriveSortOptions } from './tableContextMenuActions';

interface Props {
  onAfterAction: () => void;
}

/**
 * Column picker for the empty-area right-click, where there is no clicked column
 * to act on. Each row follows the same rules as the column right-click item:
 * picking an unsorted column appends a level, picking a sorted one reverses it,
 * and the remove button drops just that level.
 */
const SortBySubmenu = ({ onAfterAction }: Props) => {
  const ctx = useGeneratikTableContext();
  const { sortActions } = ctx;

  const options = useMemo(
    () => ctx.sortOptions ?? deriveSortOptions(ctx.processedColumns),
    [ctx.sortOptions, ctx.processedColumns],
  );

  return (
    <ContextSubmenu
      icon={<ContextMenuSortIcon />}
      label={ctx.t('sort_by')}
      testId={CONTEXT_MENU_TESTIDS.sortBy}
      onAfterAction={onAfterAction}
    >
      {(close) => [
        ...(sortActions.sortModel.length > 0
          ? [
              <ContextMenuItem
                key="__none"
                onClick={() => {
                  sortActions.clear();
                  close();
                }}
                data-testid={CONTEXT_MENU_TESTIDS.sortNone}
              >
                <ListItemText>{ctx.t('sort_none')}</ListItemText>
              </ContextMenuItem>,
            ]
          : []),
        ...options.map((option) => {
          const item = buildSortMenuItem(sortActions, option.field);

          const row = (
            <ContextMenuItem
              key={option.field}
              selected={item.isSorted}
              disabled={item.disabled}
              onClick={() => {
                item.apply();
                close();
              }}
              data-testid={sortOptionTestId(option.field)}
            >
              <ListItemText>{option.label}</ListItemText>
              {item.priority !== undefined && (
                <Box
                  component="span"
                  sx={sortLevelBadgeSx}
                  data-testid={sortPriorityTestId(option.field)}
                >
                  {item.priority}
                </Box>
              )}
              {item.direction === 'asc' && (
                <ArrowUpwardIcon sx={{ fontSize: CONTEXT_MENU_DESIGN.iconSize }} />
              )}
              {item.direction === 'desc' && (
                <ArrowDownwardIcon sx={{ fontSize: CONTEXT_MENU_DESIGN.iconSize }} />
              )}
              {item.remove && (
                <Tooltip title={ctx.t('remove_from_sort')}>
                  <IconButton
                    size="small"
                    sx={sortLevelIconButtonSx}
                    onClick={(event) => {
                      event.stopPropagation();
                      item.remove?.();
                      close();
                    }}
                    data-testid={sortRemoveTestId(option.field)}
                  >
                    <CloseIcon />
                  </IconButton>
                </Tooltip>
              )}
            </ContextMenuItem>
          );

          // A disabled MenuItem swallows pointer events, so the cap tooltip needs
          // a wrapper to hang off of.
          if (!item.disabled) return row;

          return (
            <Tooltip
              key={option.field}
              title={ctx.t('sort_limit_reached', { count: MAX_SORT_COLUMNS })}
              placement="right"
              disableInteractive
            >
              <span>{row}</span>
            </Tooltip>
          );
        }),
      ]}
    </ContextSubmenu>
  );
};

export default SortBySubmenu;
